import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { FormControlLabel, Checkbox } from '@mui/material';

import { PlanHeader } from '../components/common/plan-header';
import { PlanActionsButton } from '../components/common/actions-button';
import { HandsontableCommon } from '../components/common/handsontable-common';
import { PlanCalendarSection } from '../components/plan-calendar-section';
import { PropertySection } from '../components/confirm/property-section';
import { getPropertySectionDisplayName } from '../utils/property-display';

import type { SearchOption } from '@/types/search';
import type { CreatePlanRequest, PropertyPlansApiResponse } from '../types/plan.types';
import type { PlanTableData, RowsWithErrors } from '../components/common/handsontable.types';

import { CalendarBoxStyles } from '@/components/styles/common-style';

const FilterSectionBox = ({
  title,
  count,
  isShow,
  isDisabled,
  setIsShow,
}: {
  title: string;
  count?: number;
  isShow: boolean;
  isDisabled?: boolean;
  setIsShow: (isShow: boolean) => void;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '14px',
      }}
    >
      <Box>
        <Typography sx={{ verticalAlign: 'middle' }} component='span'>
          {title}
        </Typography>
        {count !== undefined && count !== null && (
          <>
            <Typography
              sx={{
                color: '#0047BA',
                width: '50px',
                textAlign: 'center',
                display: 'inline-block',
                backgroundColor: '#0047BA33',
                borderRadius: '1000px',
                marginLeft: '8px',
                marginRight: '8px',
                height: '26px',
              }}
              component='span'
            >
              <Typography sx={{ fontSize: '12px' }} component='span'>
                {count}
              </Typography>
            </Typography>
            <Typography sx={{ verticalAlign: 'middle' }} component='span'>
              件
            </Typography>
          </>
        )}
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={isShow}
              disabled={isDisabled}
              onChange={(e) => setIsShow(e.target.checked)}
            />
          }
          label='表示'
        />
      </Box>
    </Box>
  );
};

interface PlanEditViewProps {
  planDetail: PropertyPlansApiResponse | null;
  filteredPlanDeliveries?: CreatePlanRequest[];
  tableData: PlanTableData;
  fullTableData?: PlanTableData; // Full tableData (all records) for calendar to show dots immediately
  visibleRowIndices?: Set<number>; // Indices of rows that are currently visible (to track edited dates)
  editedRowIndices?: Set<number>; // Indices of rows that have been edited by user (to show dots for edited dates)
  oldRecordIndices?: Set<number>; // Indices of rows that are "old records" (past dates from initial load)
  plantOptions: SearchOption[];
  witnessCompanyOptions: SearchOption[];
  rowsWithErrors: RowsWithErrors;
  setRowsWithErrors: (errors: RowsWithErrors) => void;
  countPlans: { present: number; past: number };
  filters: { isShowPast: boolean; isShowArchive: boolean };
  setFilters: (filters: { isShowPast: boolean; isShowArchive: boolean }) => void;
  handleAddRowAbove: (index: number) => void;
  handleAddRowBelow: (index: number) => void;
  handleDeleteRows: (indices: number[]) => void;
  handleTableDataChange: (data: PlanTableData) => void;
  handleCancel: () => void;
  handleSubmit: () => void;
  handleAddRow: () => void;
  hasChanges: boolean;
}

export const PlanEditView: React.FC<PlanEditViewProps> = ({
  planDetail,
  filteredPlanDeliveries,
  tableData,
  fullTableData,
  visibleRowIndices,
  editedRowIndices,
  oldRecordIndices,
  plantOptions,
  witnessCompanyOptions,
  rowsWithErrors,
  setRowsWithErrors,
  countPlans,
  filters,
  setFilters,
  handleAddRowAbove,
  handleAddRowBelow,
  handleDeleteRows,
  handleTableDataChange,
  handleCancel,
  handleSubmit,
  handleAddRow,
  hasChanges,
}) => {
  return (
    <Box sx={{ width: '100%', py: 2, px: 2 }}>
      <PropertySection
        propertyName={getPropertySectionDisplayName(planDetail?.property)}
        propertyId={planDetail?.property?.id ?? ''}
      />
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left Sidebar */}
        <CalendarBoxStyles>
          {/* Calendar Section */}
          {/* IMPORTANT: Pass fullTableData, visibleRowIndices, editedRowIndices, and oldRecordIndices so calendar shows dots correctly */}
          <PlanCalendarSection
            data={filteredPlanDeliveries ?? planDetail?.planDeliveries ?? []}
            tableData={tableData as (string | number)[][]}
            fullTableData={fullTableData as (string | number)[][] | undefined}
            visibleRowIndices={visibleRowIndices}
            editedRowIndices={editedRowIndices}
            oldRecordIndices={oldRecordIndices}
            filters={filters}
          />

          {/* Filter Section */}
          <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: '500',
                mb: '20px',
              }}
            >
              フィルター
            </Typography>
            <FilterSectionBox
              title='未打設'
              count={countPlans.present}
              isShow={true}
              isDisabled={true}
              setIsShow={() => {}}
            />
            <FilterSectionBox
              title='打設済'
              count={countPlans.past}
              isShow={filters.isShowPast}
              isDisabled={false}
              setIsShow={() => {
                setFilters({ ...filters, isShowPast: !filters.isShowPast });
              }}
            />
            <FilterSectionBox
              title='アーカイブ済の予定'
              isShow={filters.isShowArchive}
              isDisabled={false}
              setIsShow={() => {
                setFilters({ ...filters, isShowArchive: !filters.isShowArchive });
              }}
            />
          </Paper>
        </CalendarBoxStyles>

        {/* Main Content */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <Box data-plan-header>
            <PlanHeader title='予定編集' subtitle='下記のリストで予定を編集してください。' />
          </Box>
          {/* Handsontable Section */}
          <HandsontableCommon
            tableData={tableData}
            plantOptions={plantOptions}
            witnessCompanyOptions={witnessCompanyOptions}
            rowsWithErrors={rowsWithErrors}
            setRowsWithErrors={setRowsWithErrors}
            onAddRowAbove={handleAddRowAbove}
            onAddRowBelow={handleAddRowBelow}
            readOnly={!planDetail?.canUpdate}
            onDeleteRows={handleDeleteRows}
            onDataChange={handleTableDataChange}
          />
        </Box>
      </Box>

      {/* Fixed Footer Actions */}
      <PlanActionsButton
        isSubmitting={false}
        isSubmitDisabled={!hasChanges}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        canUpdate={planDetail?.canUpdate || false}
        onAddRow={handleAddRow}
        submitLabel='編集内容を決定'
        isPlanDetailPage={true}
      />
    </Box>
  );
};
